<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import MessageMeta from '../MessageMeta.vue';
import { useMessageContext } from '../provider.js';
import { isValidCpf } from '../helpers/biaHandoff';

const { contentAttributes } = useMessageContext();
const { t } = useI18n();

const fields = computed(() => {
  const { reason, summary, attempts, nextAction, cpf } =
    contentAttributes.value.biaHandoff;

  const items = [
    {
      key: 'REASON',
      label: t('BIA_HANDOFF.REASON'),
      value: reason,
      icon: 'i-lucide-circle-alert',
      color: 'text-n-ruby-11',
      iconBackground: 'bg-n-ruby-3',
    },
    {
      key: 'SUMMARY',
      label: t('BIA_HANDOFF.SUMMARY'),
      value: summary,
      icon: 'i-lucide-file-text',
      color: 'text-n-iris-11',
      iconBackground: 'bg-n-iris-3',
    },
    {
      key: 'ATTEMPTS',
      label: t('BIA_HANDOFF.ATTEMPTS'),
      value: attempts,
      icon: 'i-lucide-message-circle',
      color: 'text-n-blue-11 dark:text-n-teal-11',
      iconBackground: 'bg-n-blue-3 dark:bg-n-teal-3',
    },
    {
      key: 'NEXT_ACTION',
      label: t('BIA_HANDOFF.NEXT_ACTION'),
      value: nextAction,
      icon: 'i-lucide-circle-check',
      color: 'text-n-teal-11 dark:text-n-blue-11',
      iconBackground: 'bg-n-teal-3 dark:bg-n-blue-3',
    },
  ];
  if (isValidCpf(cpf)) {
    items.push({
      key: 'CPF',
      label: t('BIA_HANDOFF.CPF'),
      value: `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`,
      icon: 'i-lucide-id-card',
      color: 'text-n-slate-11',
      iconBackground: 'bg-n-slate-3',
    });
  }
  return items;
});
</script>

<template>
  <BaseBubble
    hide-meta
    class="w-full !max-w-2xl p-3 !bg-n-iris-3 !text-n-slate-12"
    data-bubble-name="bia-handoff-card"
  >
    <section :aria-label="t('BIA_HANDOFF.TITLE')">
      <header class="flex flex-wrap items-start gap-2 pb-3">
        <span
          aria-hidden="true"
          class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-iris-5 text-n-iris-11"
        >
          <span class="i-lucide-users-round size-4" />
        </span>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-1.5">
            <h3 class="font-semibold leading-5">
              {{ t('BIA_HANDOFF.TITLE') }}
            </h3>
            <span
              class="inline-flex items-center gap-1 rounded-full bg-n-iris-5 px-2 py-0.5 text-xs font-medium text-n-iris-11"
            >
              <span aria-hidden="true" class="i-lucide-lock-keyhole size-3" />
              {{ t('BIA_HANDOFF.TEAM_ONLY') }}
            </span>
          </div>
          <p class="mt-0.5 text-xs leading-4 text-n-slate-11">
            {{ t('BIA_HANDOFF.SUBTITLE') }}
          </p>
        </div>
        <MessageMeta class="text-n-slate-11" />
      </header>
      <dl class="divide-y divide-n-weak rounded-lg bg-n-solid-1 px-3">
        <div
          v-for="field in fields"
          :key="field.key"
          class="grid min-w-0 grid-cols-1 gap-1 py-2.5 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3"
        >
          <dt
            class="flex items-center gap-2 text-xs font-semibold"
            :class="field.color"
          >
            <span
              aria-hidden="true"
              class="flex size-5 shrink-0 items-center justify-center rounded-full"
              :class="field.iconBackground"
            >
              <span class="size-3.5" :class="field.icon" />
            </span>
            {{ field.label }}
          </dt>
          <dd class="min-w-0 whitespace-pre-wrap break-words text-sm">
            {{ field.value }}
          </dd>
        </div>
      </dl>
    </section>
  </BaseBubble>
</template>
